import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import FilterNumericInput from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterNumericInput";
import FilterMenuItem from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterMenuItem";
import { NumericCondition } from "@/pages/deals/numericCondition";
import { NumericFilterValue } from "@/components/controls/ToolbarActions/ToolbarFilterButton/types";
import {
  getNumericCondition,
  getNumericAmount,
  getNumericMinAmount,
  getNumericMaxAmount
} from "@/hooks/useFilterOptions";
import { fromPercentInput, toPercentInput } from "@/utils/numbers";

const TRANSLATION_PREFIX = "datatoolbar.filters";

interface Props {
  filterOption: DataTableFilter;
  filterLabel: string;
  filters: Filters;
  setFilters: FiltersSetter;
  onClose?: () => void;
  excludeBetween?: boolean;
  skipConditionSelection?: boolean;
}

const FilterNumericSubmenu: React.FC<Props> = ({
  filterOption,
  filterLabel,
  filters,
  setFilters,
  onClose,
  excludeBetween = false,
  skipConditionSelection = false
}) => {
  const { t } = useTranslation();
  const filterKey = filterOption.key;
  const allConditionOptions = Object.values(
    NumericCondition
  ) as NumericCondition[];

  const conditionOptions = excludeBetween
    ? allConditionOptions.filter((opt) => opt !== NumericCondition.BETWEEN)
    : allConditionOptions;

  const currentValue = filters[filterKey as keyof Filters] as
    | NumericFilterValue
    | undefined;

  const isPercentage =
    filterOption.filterType.type === "numeric" &&
    filterOption.filterType.isPercentage === true;

  // Stored values are fractions (0.3); the input always works in 1–100 space.
  const displayAmount = (
    raw: number | string | null | undefined
  ): number | string => {
    if (raw === null || raw === undefined || raw === "") return "";
    return isPercentage ? (toPercentInput(raw) ?? "") : raw;
  };

  const valueToStore = (input: number): number =>
    isPercentage ? (fromPercentInput(input) ?? input) : input;

  const initialCondition = getNumericCondition(currentValue);

  const [selectedCondition, setSelectedCondition] =
    useState<NumericCondition | null>(initialCondition);
  const [showAmountInput, setShowAmountInput] = useState(
    skipConditionSelection && initialCondition !== null
  );

  const [minValue, setMinValue] = useState<string>(
    skipConditionSelection
      ? String(displayAmount(getNumericMinAmount(currentValue)) ?? "")
      : ""
  );
  const [maxValue, setMaxValue] = useState<string>(
    skipConditionSelection
      ? String(displayAmount(getNumericMaxAmount(currentValue)) ?? "")
      : ""
  );

  const amountRef = useRef<HTMLInputElement>(null);
  const minAmountRef = useRef<HTMLInputElement>(null);
  const maxAmountRef = useRef<HTMLInputElement>(null);

  const isBetweenInvalid = (): boolean => {
    if (selectedCondition !== NumericCondition.BETWEEN) return false;
    const min = minValue ? Number(minValue) : null;
    const max = maxValue ? Number(maxValue) : null;
    if (min !== null && max !== null && !isNaN(min) && !isNaN(max)) {
      return min >= max;
    }
    return false;
  };

  useEffect(() => {
    if (showAmountInput) {
      if (selectedCondition === NumericCondition.BETWEEN) {
        minAmountRef.current?.focus();
      } else {
        amountRef.current?.focus();
      }
    }
  }, [showAmountInput, selectedCondition]);

  const handleSelectCondition = (condition: NumericCondition) => {
    setSelectedCondition(condition);
    setShowAmountInput(true);
  };

  const getRefValue = (
    ref: React.RefObject<HTMLInputElement>
  ): number | null => {
    return ref.current?.value ? Number(ref.current.value) : null;
  };

  const handleApply = () => {
    if (!selectedCondition || isBetweenInvalid()) return;
    let filterValue: NumericFilterValue | null = null;
    if (selectedCondition === NumericCondition.BETWEEN) {
      const minValue = getRefValue(minAmountRef);
      const maxValue = getRefValue(maxAmountRef);

      if (minValue === null && maxValue === null) {
        return;
      }

      filterValue = {};
      if (minValue !== null && !isNaN(minValue)) {
        filterValue.min = valueToStore(minValue);
      }
      if (maxValue !== null && !isNaN(maxValue)) {
        filterValue.max = valueToStore(maxValue);
      }
    } else {
      const amountValue = getRefValue(amountRef);

      if (amountValue === null || isNaN(amountValue)) {
        return;
      }

      filterValue = { [selectedCondition]: valueToStore(amountValue) };
    }

    setFilters((prev) => ({
      ...prev,
      [filterKey]: filterValue
    }));
    onClose?.();
  };

  const handleCancel = () => {
    if (showAmountInput && !skipConditionSelection) {
      setShowAmountInput(false);
      setSelectedCondition(null);
    } else {
      onClose?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    }
  };

  const getConditionLabel = (condition: NumericCondition) =>
    t(`${TRANSLATION_PREFIX}.MRRConditions.${condition}`);

  if (showAmountInput && selectedCondition) {
    const conditionLabel = getConditionLabel(selectedCondition);
    const shouldPreFillValue = skipConditionSelection;

    return (
      <div className="flex flex-col gap-1 min-w-[200px]">
        <Text variant="bodySecondary" className="w-full p-2 pr-3 pb-[2px]">
          {`${filterLabel} ${conditionLabel.toLowerCase()}`}
        </Text>
        <div className="flex flex-col gap-2 py-2 px-1">
          {selectedCondition === NumericCondition.BETWEEN ? (
            <div className="flex gap-2">
              <FilterNumericInput
                ref={minAmountRef}
                placeholder={t(`${TRANSLATION_PREFIX}.amount`)}
                defaultValue={
                  shouldPreFillValue
                    ? displayAmount(getNumericMinAmount(currentValue))
                    : ""
                }
                onKeyDown={handleKeyDown}
                onChange={(e) => setMinValue(e.target.value)}
              />
              <FilterNumericInput
                ref={maxAmountRef}
                placeholder={t(`${TRANSLATION_PREFIX}.amount`)}
                defaultValue={
                  shouldPreFillValue
                    ? displayAmount(getNumericMaxAmount(currentValue))
                    : ""
                }
                onKeyDown={handleKeyDown}
                onChange={(e) => setMaxValue(e.target.value)}
              />
            </div>
          ) : (
            <FilterNumericInput
              ref={amountRef}
              placeholder={t(`${TRANSLATION_PREFIX}.setAmount`)}
              defaultValue={
                shouldPreFillValue
                  ? displayAmount(getNumericAmount(currentValue))
                  : ""
              }
              onKeyDown={handleKeyDown}
            />
          )}
        </div>
        <div className="flex justify-end gap-2 p-1">
          <Button onClick={handleCancel} variant="secondary">
            {t("cancel")}
          </Button>
          <Button onClick={handleApply} disabled={isBetweenInvalid()}>
            {t("apply")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 min-w-[160px]">
      {conditionOptions.map((option) => (
        <FilterMenuItem
          key={option}
          onClick={() => handleSelectCondition(option)}
        >
          <Text variant="body">{getConditionLabel(option)}</Text>
        </FilterMenuItem>
      ))}
    </div>
  );
};

export default FilterNumericSubmenu;
